import { ChevronDown } from 'lucide-react';
import React from 'react';

import { useSidebar, useAuth } from '../app/providers';

import { SidebarGroupConfig } from './sidebarConfig';
import SidebarItem from './SidebarItem';

interface SidebarGroupProps {
    group: SidebarGroupConfig;
    badgeCounts?: Record<string, number | undefined> | undefined;
}

const SidebarGroup: React.FC<SidebarGroupProps> = ({ group, badgeCounts }) => {
    const { isCollapsed, toggleGroup, isGroupExpanded } = useSidebar();
    const { isSuperAdmin } = useAuth();
    const isExpanded = isGroupExpanded(group.id);
    const Icon = group.icon;

    // Filter items based on role
    const visibleItems = group.items.filter(item => {
        if (item.superAdminOnly && !isSuperAdmin()) {
            return false;
        }
        return true;
    });

    if (visibleItems.length === 0) {
        return null;
    }

    if (isCollapsed) {
        // In collapsed mode, show as a simple icon with tooltip
        return (
            <div className="relative group/tooltip">
                <button
                    onClick={() => toggleGroup(group.id)}
                    className="flex items-center justify-center w-full p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                >
                    <Icon size={18} />
                </button>
                {/* Tooltip */}
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                    {group.label}
                </div>
            </div>
        );
    }

    return (
        <div className="mb-1">
            <button
                onClick={() => toggleGroup(group.id)}
                className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <Icon size={18} className="flex-shrink-0" />
                    <span className="font-medium text-sm">{group.label}</span>
                </div>
                <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Expandable items */}
            <div
                className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
            >
                <div className="py-1 space-y-0.5">
                    {visibleItems.map((item) => (
                        <SidebarItem
                            key={item.id}
                            item={item}
                            isNested
                            badgeCount={badgeCounts?.[item.id]}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SidebarGroup;
